/**
 * Driver Earnings Dashboard
 * Unified view of all driver earnings from rides and packages
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, 
  DollarSign, 
  TrendingUp, 
  Package, 
  Car,
  Clock,
  CheckCircle,
  CreditCard,
  Calendar,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DriverEarnings = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('today');

  useEffect(() => {
    fetchDashboard();
  }, [token]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/driver/earnings/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to load earnings');
      
      const data = await response.json();
      setDashboard(data.dashboard);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-JM', {
      style: 'currency',
      currency: 'JMD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getPeriodData = () => {
    if (!dashboard) return null;
    return dashboard[selectedPeriod] || dashboard.today;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-emerald-50 to-white">
        <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-red-50 to-white p-6">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={() => navigate('/driver')} variant="outline">
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const periodData = getPeriodData();

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      {/* Header */}
      <div className="bg-emerald-600 text-white p-4">
        <div className="flex items-center gap-3 mb-4">
          <button 
            onClick={() => navigate('/driver')}
            className="p-2 hover:bg-emerald-700 rounded-lg transition-colors"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-xl font-bold">My Earnings</h1>
        </div>
        
        {/* Period Selector */}
        <div className="flex gap-2">
          {['today', 'week', 'month'].map((period) => (
            <button
              key={period}
              onClick={() => setSelectedPeriod(period)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedPeriod === period 
                  ? 'bg-white text-emerald-600' 
                  : 'bg-emerald-700 text-emerald-100 hover:bg-emerald-800'
              }`}
              data-testid={`period-${period}`}
            >
              {period.charAt(0).toUpperCase() + period.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Main Earnings Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6" data-testid="earnings-summary">
          <div className="text-center mb-6">
            <p className="text-gray-500 text-sm mb-1">
              {selectedPeriod === 'today' ? "Today's" : selectedPeriod === 'week' ? 'This Week\'s' : 'This Month\'s'} Earnings
            </p>
            <h2 className="text-4xl font-bold text-emerald-600" data-testid="total-earnings">
              {formatCurrency(periodData?.total_net || dashboard?.legacy_total_earnings || 0)}
            </h2>
            <p className="text-gray-400 text-sm mt-1">
              {periodData?.rides_count + periodData?.packages_count} jobs completed
            </p>
          </div>

          {/* Breakdown Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Car className="h-5 w-5 text-blue-600" />
                <span className="text-sm text-blue-600 font-medium">Rides</span>
              </div>
              <p className="text-2xl font-bold text-gray-800">{periodData?.rides_count || 0}</p>
              <p className="text-sm text-gray-500">{formatCurrency(periodData?.rides_earnings)}</p>
            </div>
            
            <div className="bg-amber-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Package className="h-5 w-5 text-amber-600" />
                <span className="text-sm text-amber-600 font-medium">Packages</span>
              </div>
              <p className="text-2xl font-bold text-gray-800">{periodData?.packages_count || 0}</p>
              <p className="text-sm text-gray-500">{formatCurrency(periodData?.packages_earnings)}</p>
            </div>
          </div>
        </div>

        {/* Pending Payout Card */}
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-sm mb-1">Pending Payout</p>
              <h3 className="text-3xl font-bold" data-testid="pending-payout">
                {formatCurrency((dashboard?.pending_payout || dashboard?.legacy_pending_earnings || 0)?.amount)}
              </h3>
              <p className="text-emerald-200 text-xs mt-1">Ready for withdrawal</p>
            </div>
            <div className="bg-white/20 rounded-full p-3">
              <CreditCard className="h-8 w-8" />
            </div>
          </div>
          {/* Withdraw Button */}
          <Button 
            onClick={() => navigate('/driver/withdrawals')}
            className="w-full mt-4 bg-white text-emerald-600 hover:bg-emerald-50 font-semibold"
            data-testid="withdraw-btn"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            Withdraw Funds
          </Button>
        </div>

        {/* Status Breakdown */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-500" />
            Earnings Status
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-yellow-600" />
                <span className="text-gray-700">Pending</span>
              </div>
              <span className="font-semibold text-gray-800">{formatCurrency(periodData?.pending_amount)}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-blue-600" />
                <span className="text-gray-700">Confirmed</span>
              </div>
              <span className="font-semibold text-gray-800">{formatCurrency(periodData?.confirmed_amount)}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-3">
                <DollarSign className="h-5 w-5 text-green-600" />
                <span className="text-gray-700">Paid</span>
              </div>
              <span className="font-semibold text-gray-800">{formatCurrency(periodData?.paid_amount)}</span>
            </div>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-emerald-500" />
            Recent Transactions
          </h3>
          <div className="space-y-3">
            {dashboard?.recent_transactions?.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No transactions yet</p>
            ) : (
              dashboard?.recent_transactions?.map((tx, index) => (
                <div 
                  key={tx.id || index}
                  className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {tx.job_type === 'ride' ? (
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <Car className="h-4 w-4 text-blue-600" />
                      </div>
                    ) : (
                      <div className="bg-amber-100 p-2 rounded-lg">
                        <Package className="h-4 w-4 text-amber-600" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-gray-800 text-sm">
                        {tx.job_type === 'ride' ? 'Ride' : 'Package'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(tx.created_at).toLocaleDateString('en-JM', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-emerald-600">+{formatCurrency(tx.net_earnings)}</p>
                    <p className={`text-xs ${
                      tx.status === 'paid' ? 'text-green-500' :
                      tx.status === 'confirmed' ? 'text-blue-500' :
                      'text-yellow-500'
                    }`}>
                      {tx.status}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {dashboard?.recent_transactions?.length > 0 && (
            <button 
              className="w-full mt-4 py-3 text-emerald-600 font-medium flex items-center justify-center gap-2 hover:bg-emerald-50 rounded-lg transition-colors"
              onClick={() => navigate('/driver/earnings/history')}
            >
              View All Transactions
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverEarnings;
